import React, { useState, useEffect } from 'react';
import { FiUser, FiMapPin, FiBell, FiEdit2, FiPlus, FiLock } from 'react-icons/fi';
import { useWholesaleAuth } from '../../contexts/WholesaleAuthContext';
import { changeWholesalePassword } from '../../services/wholesaleAuthService';
import { db } from '../../services/firebase';
import { doc, getDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import styles from './AccountSettings.module.css';

const SectionCard = ({ title, icon, children }) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <div className={styles.headerIcon}>{icon}</div>
      <h3 className={styles.headerTitle}>{title}</h3>
      <button className={styles.editButton}><FiEdit2 /> Edit</button>
    </div>
    <div className={styles.cardBody}>
      {children}
    </div>
  </div>
);

const PasswordChangeCard = () => {
  const { wholesaleCustomer } = useWholesaleAuth();
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (passwords.new !== passwords.confirm) {
      setError('New passwords do not match');
      return;
    }

    if (passwords.new.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      await changeWholesalePassword(wholesaleCustomer.id, passwords.new);
      setMessage('Password changed successfully');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      setError('Failed to change password. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.headerIcon}><FiLock /></div>
        <h3 className={styles.headerTitle}>Change Password</h3>
      </div>
      <div className={styles.cardBody}>
        {message && (
          <div className={`${styles.message} ${styles.messageSuccess}`}>
            {message}
          </div>
        )}
        {error && (
          <div className={`${styles.message} ${styles.messageError}`}>
            {error}
          </div>
        )}
        <form onSubmit={handlePasswordChange}>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>Current Password</label>
              <input 
                type="password" 
                value={passwords.current}
                onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                required 
                disabled={loading}
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>New Password</label>
              <input 
                type="password" 
                value={passwords.new}
                onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                required 
                disabled={loading}
                minLength={8}
              />
            </div>
            <div className={styles.formField}>
              <label>Confirm New Password</label>
              <input 
                type="password" 
                value={passwords.confirm}
                onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                required 
                disabled={loading}
                minLength={8}
              />
            </div>
          </div>
          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

const WholesaleAccountSettings = () => {
  const { wholesaleCustomer } = useWholesaleAuth();
  const [addresses, setAddresses] = useState([]);
  const [notifications, setNotifications] = useState({
    orderConfirmations: true,
    shipmentUpdates: true,
    promotionalEmails: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const isDemoAccount = wholesaleCustomer?.status === 'demo';

  useEffect(() => {
    const fetchAccountData = async () => {
      if (!wholesaleCustomer?.id) return;
      
      try {
        setLoading(true);
        
        if (isDemoAccount) {
          // Use hardcoded demo data
          setAddresses([
            {
              id: 'demo-1',
              type: 'Primary',
              street: '123 Commerce St',
              city: 'Doha',
              state: 'Qatar',
              zipCode: '12345'
            },
            {
              id: 'demo-2', 
              type: 'Warehouse',
              street: '456 Industrial Ave',
              city: 'Doha',
              state: 'Qatar',
              zipCode: '12346'
            }
          ]);
          setNotifications({
            orderConfirmations: true,
            shipmentUpdates: true,
            promotionalEmails: false
          });
        } else {
          // Fetch real data from Firebase
          const addressesRef = collection(db, `wholesaleCustomers/${wholesaleCustomer.id}/addresses`);
          const addressesSnapshot = await getDocs(addressesRef);
          const addressesList = addressesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setAddresses(addressesList);
          
          // Fetch notification preferences
          const customerDoc = await getDoc(doc(db, 'wholesaleCustomers', wholesaleCustomer.id));
          if (customerDoc.exists()) {
            const data = customerDoc.data();
            setNotifications({
              orderConfirmations: data.notifications?.orderConfirmations ?? true,
              shipmentUpdates: data.notifications?.shipmentUpdates ?? true,
              promotionalEmails: data.notifications?.promotionalEmails ?? false
            });
          }
        }
        
        setError('');
      } catch (err) {
        console.error('Error fetching account data:', err);
        setError('Could not load account settings.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAccountData();
  }, [wholesaleCustomer?.id, isDemoAccount]);

  const handleNotificationChange = async (key, value) => {
    if (isDemoAccount) {
      // Don't allow changes for demo accounts
      return;
    }
    
    try {
      const newNotifications = { ...notifications, [key]: value };
      setNotifications(newNotifications);
      
      // Update in Firebase
      await updateDoc(doc(db, 'wholesaleCustomers', wholesaleCustomer.id), {
        notifications: newNotifications
      });
    } catch (err) {
      console.error('Error updating notifications:', err);
      // Revert on error
      setNotifications(notifications);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>Account Settings</div>
          <div className={styles.headerSubtitle}>Manage your company profile, addresses, and preferences.</div>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Loading account settings...
        </div>
      </div>
    );
  }

  if (error && !isDemoAccount) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>Account Settings</div>
          <div className={styles.headerSubtitle}>Manage your company profile, addresses, and preferences.</div>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', background: '#f9f9f9', border: '1px solid #eee' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>Account Settings</div>
        <div className={styles.headerSubtitle}>Manage your company profile, addresses, and preferences.</div>
      </div>

      <div className={styles.settingsGrid}>
        {/* Company Profile */}
        <SectionCard title="Company Profile" icon={<FiUser />}>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>Business Name</label>
              <input type="text" defaultValue={wholesaleCustomer?.businessName || ''} readOnly />
            </div>
            <div className={styles.formField}>
              <label>Contact Person</label>
              <input type="text" defaultValue={wholesaleCustomer?.contactName || ''} readOnly />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>Email Address</label>
              <input type="email" defaultValue={wholesaleCustomer?.wholesaleEmail || ''} readOnly />
            </div>
            <div className={styles.formField}>
              <label>Account Number</label>
              <input type="text" defaultValue={wholesaleCustomer?.accountNumber || ''} readOnly />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>Phone Number</label>
              <input type="tel" defaultValue={wholesaleCustomer?.purchasePhone || ''} readOnly />
            </div>
            <div className={styles.formField}>
              <label>Status</label>
              <input type="text" defaultValue={wholesaleCustomer?.status || ''} readOnly style={{ textTransform: 'capitalize' }} />
            </div>
          </div>
        </SectionCard>

        {/* Billing Address */}
        <SectionCard title="Billing Address" icon={<FiMapPin />}>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>Address Line 1</label>
              <input 
                type="text" 
                defaultValue={isDemoAccount ? '123 Commerce St' : (wholesaleCustomer?.billingAddress?.street || '')} 
                readOnly={isDemoAccount}
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>City</label>
              <input 
                type="text" 
                defaultValue={isDemoAccount ? 'Doha' : (wholesaleCustomer?.billingAddress?.city || '')} 
                readOnly={isDemoAccount}
              />
            </div>
            <div className={styles.formField}>
              <label>State / Province</label>
              <input 
                type="text" 
                defaultValue={isDemoAccount ? 'Qatar' : (wholesaleCustomer?.billingAddress?.state || '')} 
                readOnly={isDemoAccount}
              />
            </div>
            <div className={styles.formField}>
              <label>Zip / Postal Code</label>
              <input 
                type="text" 
                defaultValue={isDemoAccount ? '12345' : (wholesaleCustomer?.billingAddress?.zipCode || '')} 
                readOnly={isDemoAccount}
              />
            </div>
          </div>
        </SectionCard>
        
        {/* Shipping Addresses */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.headerIcon}><FiMapPin /></div>
            <h3 className={styles.headerTitle}>Shipping Addresses</h3>
            {!isDemoAccount && <button className={styles.addButton}><FiPlus /> Add New</button>}
          </div>
          <div className={styles.cardBody}>
            {addresses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                No shipping addresses found
              </div>
            ) : (
              addresses.map((address) => (
                <div key={address.id} className={styles.addressItem}>
                  <div>
                    <div className={styles.addressLabel}>{address.type || 'Address'}</div>
                    <p>{address.street}, {address.city}, {address.state} {address.zipCode}</p>
                  </div>
                  {!isDemoAccount && (
                    <div className={styles.addressActions}>
                      <button>Edit</button>
                      <button>Delete</button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Notification Settings */}
        <SectionCard title="Notification Settings" icon={<FiBell />}>
          <div className={styles.toggleRow}>
            <div>
              <div className={styles.toggleLabel}>Order Confirmations</div>
              <p className={styles.toggleDescription}>Receive an email every time an order is placed.</p>
            </div>
            <label className={styles.switch}>
              <input 
                type="checkbox" 
                checked={notifications.orderConfirmations}
                onChange={(e) => handleNotificationChange('orderConfirmations', e.target.checked)}
                disabled={isDemoAccount}
              />
              <span className={styles.slider}></span>
            </label>
          </div>
          <div className={styles.toggleRow}>
            <div>
              <div className={styles.toggleLabel}>Shipment Updates</div>
              <p className={styles.toggleDescription}>Get notified when your order has shipped.</p>
            </div>
            <label className={styles.switch}>
              <input 
                type="checkbox" 
                checked={notifications.shipmentUpdates}
                onChange={(e) => handleNotificationChange('shipmentUpdates', e.target.checked)}
                disabled={isDemoAccount}
              />
              <span className={styles.slider}></span>
            </label>
          </div>
          <div className={styles.toggleRow}>
            <div>
              <div className={styles.toggleLabel}>Promotional Emails</div>
              <p className={styles.toggleDescription}>Receive news about new products and special offers.</p>
            </div>
            <label className={styles.switch}>
              <input 
                type="checkbox" 
                checked={notifications.promotionalEmails}
                onChange={(e) => handleNotificationChange('promotionalEmails', e.target.checked)}
                disabled={isDemoAccount}
              />
              <span className={styles.slider}></span>
            </label>
          </div>
        </SectionCard>
        
        {/* Password Change */}
        {!isDemoAccount && <PasswordChangeCard />}
      </div>
    </div>
  );
};

export default WholesaleAccountSettings; 